/**
 * EmptyState — reusable empty-state pattern for tables/lists/dashboards.
 * Replaces bare "Tiada rekod" text with a friendly illustration block +
 * optional action CTA.
 *
 * Usage:
 *   <EmptyState
 *     icon={Inbox}
 *     title="Belum ada permohonan cuti bulan ini."
 *     hint="Staf belum mohon cuti untuk bulan semasa."
 *     actionLabel="Buat permohonan cuti"
 *     onAction={() => navigate('/me')}
 *   />
 */
import { Inbox } from 'lucide-react';

const EmptyState = ({
  icon: Icon = Inbox,
  title = 'Tiada rekod',
  hint,
  actionLabel,
  onAction,
  tone = 'default', // 'default' | 'subtle' | 'info' | 'success'
  compact = false,
  className = '',
  testId = 'empty-state',
}) => {
  const tones = {
    default: { bg: 'bg-gray-50', ring: 'ring-1 ring-gray-100', iconBg: 'bg-white', iconColor: 'text-gray-400', titleColor: 'text-gray-700', hintColor: 'text-gray-500' },
    subtle:  { bg: 'bg-transparent', ring: '', iconBg: 'bg-gray-100', iconColor: 'text-gray-400', titleColor: 'text-gray-600', hintColor: 'text-gray-400' },
    info:    { bg: 'bg-indigo-50', ring: 'ring-1 ring-indigo-100', iconBg: 'bg-white', iconColor: 'text-indigo-500', titleColor: 'text-indigo-900', hintColor: 'text-indigo-700/70' },
    success: { bg: 'bg-emerald-50', ring: 'ring-1 ring-emerald-100', iconBg: 'bg-white', iconColor: 'text-emerald-600', titleColor: 'text-emerald-900', hintColor: 'text-emerald-700/80' },
  };
  const t = tones[tone] || tones.default;

  return (
    <div data-testid={testId}
      className={`${t.bg} ${t.ring} rounded-2xl ${compact ? 'p-5' : 'p-8 sm:p-10'} flex flex-col items-center text-center ${className}`}>
      <div className={`w-14 h-14 rounded-full ${t.iconBg} ring-1 ring-gray-100 flex items-center justify-center mb-3 shadow-sm`}>
        <Icon className={`w-6 h-6 ${t.iconColor}`} strokeWidth={1.6} />
      </div>
      <h3 className={`text-sm sm:text-base font-semibold ${t.titleColor}`}>{title}</h3>
      {hint && <p className={`text-xs sm:text-sm mt-1 max-w-md ${t.hintColor}`}>{hint}</p>}
      {actionLabel && onAction && (
        <button
          onClick={onAction}
          data-testid="empty-state-action"
          className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold bg-red-800 text-white hover:bg-red-900 transition-colors"
        >
          {actionLabel}
        </button>
      )}
    </div>
  );
};

export default EmptyState;
